/**
 * The frame for a page reached from Clock In — the own-shift edit and the
 * missed shift. Same palette and type as ClockInScreen (its `C` tokens and
 * `MI` icon), a plain back arrow and a title, one column, full width up to the
 * screen's own max. These were bottom sheets over the list; forms are their
 * own pages now, never a panel or a sheet beside or over a list.
 *
 * Both are mounted beside each ClockInScreen route (`/field/clock-in` in the
 * two field trees, `/payroll/clock-in` in the office one), so the mount is
 * read from the path rather than assumed.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { C, MI } from './clockInTheme';

/** `/field/clock-in/shift/x/edit` → `/field/clock-in`. */
export function clockInBaseOf(pathname: string): string {
  const i = pathname.indexOf('/clock-in');
  return i >= 0 ? pathname.slice(0, i + '/clock-in'.length) : '/field/clock-in';
}

/**
 * Where these pages return to: the list the hand came from (`listUrl` in the
 * navigation state, set by ClockInScreen), else that mount's "This period".
 * Only a URL under the same mount is trusted.
 */
export function useClockInReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const base = clockInBaseOf(location.pathname);
  const fromState = (location.state as { listUrl?: unknown } | null)?.listUrl;
  const listUrl = typeof fromState === 'string' && fromState.startsWith(base) ? fromState : `${base}?view=period`;
  return {
    base,
    listUrl,
    /** Back to the list; `flash` is the line ClockInScreen shows once on arrival. */
    back: (flash?: string) => navigate(listUrl, { replace: true, state: flash ? { flash } : undefined }),
  };
}

export function ClockInPageShell({
  title,
  subtitle,
  onBack,
  children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  onBack: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-full font-body pb-[var(--bottom-chrome)] ci-dark-override" style={{ background: C.bg, color: C.onSurface }}>
      <div className="px-5 pt-4 pb-4 max-w-lg mx-auto w-full">
        <button type="button" aria-label="Back" onClick={onBack} className="mb-4" style={{ color: C.onSurfaceVariant }}>
          <MI name="arrow_back" size={24} />
        </button>
        <h1 className="font-headline text-2xl font-semibold" style={{ color: C.onSurface }}>{title}</h1>
        {subtitle && <p className="text-sm mt-1" style={{ color: C.onSurfaceVariant }}>{subtitle}</p>}
      </div>
      <div className="px-5 max-w-lg mx-auto w-full">{children}</div>
    </div>
  );
}

export const FIELD_LABEL = 'block text-[11px] font-semibold uppercase tracking-widest mb-2';
export const FIELD_INPUT = 'w-full rounded-xl px-4 py-3 text-sm';
export const fieldInputStyle: React.CSSProperties = {
  background: C.bg, border: '1px solid var(--ci-edge-25)', color: C.onSurface, outline: 'none',
};

export function FormError({ children }: { children: React.ReactNode }) {
  return (
    <div role="alert" className="text-sm mt-4 rounded-xl px-4 py-3" style={{ background: 'var(--ci-red-wash)', color: C.errorInk }}>
      {children}
    </div>
  );
}

export function PrimaryButton({ busy, disabled, onClick, children }: { busy: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      className="w-full rounded-xl py-4 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-all"
      style={{ background: 'var(--ci-confirm)', color: 'var(--ci-on-confirm)' }}
    >
      {busy ? <MI name="progress_activity" size={20} className="animate-spin" /> : <span>{children}</span>}
    </button>
  );
}

export function SecondaryButton({ disabled, onClick, children }: { disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full rounded-xl py-4 font-medium text-sm active:scale-[0.98] transition-all"
      style={{ border: '1px solid var(--ci-edge-19)', color: C.onSurfaceVariant, background: 'transparent' }}
    >
      {children}
    </button>
  );
}
